// Content management page

import React, { useState } from 'react'
import { useContent } from '../../hooks/useApi'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { Select } from '../../components/ui/Select'

// Mock data for enhanced content
const mockContent = [
  {
    id: 1,
    title: "Tech Innovation Spotlight",
    contentText: "Showcase your latest product features with engaging visuals and customer testimonials. Perfect for LinkedIn and Twitter.",
    platform: "LinkedIn",
    status: "Published",
    type: "Post",
    engagement: 1247,
    scheduledDate: "2024-01-15T10:30:00Z",
    publishedDate: "2024-01-15T10:30:00Z",
    tags: ["Tech", "Innovation", "Product"],
    imageUrl: "https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=400&h=300&fit=crop"
  },
  {
    id: 2,
    title: "Behind-the-Scenes Culture",
    contentText: "Share your company culture and team dynamics. Great for Instagram Stories and LinkedIn posts.",
    platform: "Instagram",
    status: "Scheduled",
    type: "Story",
    engagement: 892,
    scheduledDate: "2024-01-16T14:00:00Z",
    publishedDate: null,
    tags: ["Culture", "Team", "Behind-the-scenes"],
    imageUrl: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&h=300&fit=crop"
  },
  {
    id: 3,
    title: "Industry Insights & Trends",
    contentText: "Share your expertise on current industry trends and provide valuable insights to your audience.",
    platform: "Twitter",
    status: "Draft",
    type: "Thread",
    engagement: 0,
    scheduledDate: null,
    publishedDate: null,
    tags: ["Industry", "Trends", "Expertise"],
    imageUrl: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=300&fit=crop"
  },
  {
    id: 4,
    title: "Customer Success Story",
    contentText: "Highlight a customer success story with metrics and testimonials. Perfect for case study promotion.",
    platform: "LinkedIn",
    status: "Published",
    type: "Article",
    engagement: 2156,
    scheduledDate: "2024-01-14T09:00:00Z",
    publishedDate: "2024-01-14T09:00:00Z",
    tags: ["Customer", "Success", "Case Study"],
    imageUrl: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=300&fit=crop"
  },
  {
    id: 5,
    title: "Product Tutorial Video",
    contentText: "Create a short tutorial video showcasing your product features and benefits.",
    platform: "YouTube",
    status: "Published",
    type: "Video",
    engagement: 3421,
    scheduledDate: "2024-01-13T15:00:00Z",
    publishedDate: "2024-01-13T15:00:00Z",
    tags: ["Tutorial", "Video", "Product"],
    imageUrl: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=300&fit=crop"
  },
  {
    id: 6,
    title: "Team Achievement Celebration",
    contentText: "Celebrate team milestones and achievements with engaging visuals and motivational content.",
    platform: "Instagram",
    status: "Draft",
    type: "Post",
    engagement: 0,
    scheduledDate: null,
    publishedDate: null,
    tags: ["Team", "Achievement", "Celebration"],
    imageUrl: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=300&fit=crop"
  }
]

const ContentPage: React.FC = () => {
  const { data: contentData, isLoading, error } = useContent()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedPlatform, setSelectedPlatform] = useState('all')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [selectedType, setSelectedType] = useState('all')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')

  // Use mock data for now, fallback to API data
  const content = mockContent

  const platforms = ['all', 'LinkedIn', 'Twitter', 'Instagram', 'Facebook', 'YouTube']
  const statuses = ['all', 'Published', 'Scheduled', 'Draft', 'Failed']
  const types = ['all', 'Post', 'Story', 'Article', 'Video', 'Thread']

  const filteredContent = content.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.contentText.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesPlatform = selectedPlatform === 'all' || item.platform === selectedPlatform
    const matchesStatus = selectedStatus === 'all' || item.status === selectedStatus
    const matchesType = selectedType === 'all' || item.type === selectedType
    
    return matchesSearch && matchesPlatform && matchesStatus && matchesType
  })

  const getStatusColor = (status: string) => {
    const colors = {
      Published: 'bg-green-500/10 text-green-600 border-green-500/20',
      Scheduled: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
      Draft: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
      Failed: 'bg-red-500/10 text-red-600 border-red-500/20'
    }
    return colors[status as keyof typeof colors] || 'bg-gray-500/10 text-gray-600 border-gray-500/20'
  }

  const getPlatformIcon = (platform: string) => {
    const icons = {
      LinkedIn: () => (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      ),
      Twitter: () => (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
        </svg>
      ),
      Instagram: () => (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987 6.62 0 11.987-5.367 11.987-11.987C24.014 5.367 18.637.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.895 3.708 13.744 3.708 12.447s.49-2.448 1.297-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.807.875 1.297 2.026 1.297 3.323s-.49 2.448-1.297 3.323c-.875.807-2.026 1.297-3.323 1.297zm7.718-1.297c-.875.807-2.026 1.297-3.323 1.297s-2.448-.49-3.323-1.297c-.807-.875-1.297-2.026-1.297-3.323s.49-2.448 1.297-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.807.875 1.297 2.026 1.297 3.323s-.49 2.448-1.297 3.323z"/>
        </svg>
      ),
      Facebook: () => (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      ),
      YouTube: () => (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
        </svg>
      )
    }
    return icons[platform as keyof typeof icons] || (() => <div className="w-5 h-5 bg-gray-300 rounded"></div>)
  }

  const formatEngagement = (engagement: number) => {
    if (engagement >= 1000000) return `${(engagement / 1000000).toFixed(1)}M`
    if (engagement >= 1000) return `${(engagement / 1000).toFixed(1)}K`
    return engagement.toString()
  }

  return (
    <div className="space-y-8 fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
        <div>
          <h1 className="text-4xl font-bold gradient-text">
            Content Library
          </h1>
          <p className="text-lg text-muted-foreground mt-2">
            Manage and organize your social media content
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Button variant="outline" className="btn-outline">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export
          </Button>
          <Button className="btn-primary">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Create Content
          </Button>
        </div>
      </div>

      {/* Filters and Search */}
      <Card className="card-modern slide-up" style={{ animationDelay: '0.1s' }}>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium mb-2">Search Content</label>
              <Input
                type="text"
                placeholder="Search content..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-modern"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Platform</label>
              <Select
                value={selectedPlatform}
                onChange={(e) => setSelectedPlatform(e.target.value)}
                className="w-full"
              >
                {platforms.map(platform => (
                  <option key={platform} value={platform}>
                    {platform === 'all' ? 'All Platforms' : platform}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Status</label>
              <Select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full"
              >
                {statuses.map(status => (
                  <option key={status} value={status}>
                    {status === 'all' ? 'All Status' : status}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Type</label>
              <Select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full"
              >
                {types.map(type => (
                  <option key={type} value={type}>
                    {type === 'all' ? 'All Types' : type}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">View</label>
              <div className="flex border border-border rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`flex-1 px-3 py-2 text-sm transition-colors ${
                    viewMode === 'grid' 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-background hover:bg-muted'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`flex-1 px-3 py-2 text-sm transition-colors ${
                    viewMode === 'list' 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-background hover:bg-muted'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="stats-card slide-up" style={{ animationDelay: '0.2s' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="stats-label">Total Content</p>
              <p className="stats-value">{filteredContent.length}</p>
            </div>
            <div className="p-3 bg-blue-500/10 rounded-xl">
              <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          </div>
        </Card>
        
        <Card className="stats-card slide-up" style={{ animationDelay: '0.3s' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="stats-label">Published</p>
              <p className="stats-value">
                {filteredContent.filter(c => c.status === 'Published').length}
              </p>
            </div>
            <div className="p-3 bg-green-500/10 rounded-xl">
              <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
        </Card>
        
        <Card className="stats-card slide-up" style={{ animationDelay: '0.4s' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="stats-label">Total Engagement</p>
              <p className="stats-value">
                {formatEngagement(filteredContent.reduce((acc, item) => acc + item.engagement, 0))}
              </p>
            </div>
            <div className="p-3 bg-purple-500/10 rounded-xl">
              <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
          </div>
        </Card>
        
        <Card className="stats-card slide-up" style={{ animationDelay: '0.5s' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="stats-label">Scheduled</p>
              <p className="stats-value">
                {filteredContent.filter(c => c.status === 'Scheduled').length}
              </p>
            </div>
            <div className="p-3 bg-orange-500/10 rounded-xl">
              <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </Card>
      </div>

      {/* Content Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredContent.map((item, index) => (
            <Card key={item.id} className="card-modern hover-lift slide-up" style={{ animationDelay: `${0.6 + index * 0.1}s` }}>
              <div className="relative">
                <img 
                  src={item.imageUrl} 
                  alt={item.title}
                  className="w-full h-48 object-cover rounded-t-xl"
                />
                <div className="absolute top-3 right-3">
                  <Badge className={`text-xs border ${getStatusColor(item.status)}`}>
                    {item.status}
                  </Badge>
                </div>
                <div className="absolute top-3 left-3">
                  <div className="w-8 h-8 bg-white/90 rounded-lg flex items-center justify-center shadow-lg">
                    {getPlatformIcon(item.platform)()}
                  </div>
                </div>
              </div>
              
              <CardHeader className="pb-4">
                <CardTitle className="text-lg mb-2 line-clamp-2">{item.title}</CardTitle>
                <CardDescription className="line-clamp-3">
                  {item.contentText}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {item.platform}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {item.type}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    <span className="text-sm font-medium">{formatEngagement(item.engagement)}</span>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-1">
                  {item.tags.slice(0, 3).map(tag => (
                    <span key={tag} className="px-2 py-1 bg-muted/50 rounded-md text-xs text-muted-foreground">
                      {tag}
                    </span>
                  ))}
                </div>
                
                <div className="flex items-center justify-between pt-2 border-t border-border/50">
                  <span className="text-xs text-muted-foreground">
                    {item.publishedDate 
                      ? new Date(item.publishedDate).toLocaleDateString()
                      : item.scheduledDate 
                        ? `Scheduled: ${new Date(item.scheduledDate).toLocaleDateString()}`
                        : 'Draft'
                    }
                  </span>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" className="btn-outline">
                      Edit
                    </Button>
                    <Button size="sm" className="btn-primary">
                      View
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredContent.map((item, index) => (
            <Card key={item.id} className="card-modern hover-lift slide-up" style={{ animationDelay: `${0.6 + index * 0.1}s` }}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <img 
                    src={item.imageUrl} 
                    alt={item.title}
                    className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                  />
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="text-lg font-semibold mb-1">{item.title}</h3>
                        <p className="text-muted-foreground line-clamp-2">{item.contentText}</p>
                      </div>
                      <Badge className={`text-xs border ${getStatusColor(item.status)}`}>
                        {item.status}
                      </Badge>
                    </div>
                    
                    <div className="flex items-center gap-4 mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-primary/10 rounded-lg flex items-center justify-center">
                          {getPlatformIcon(item.platform)()}
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {item.platform}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {item.type}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1">
                        <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        <span className="text-sm font-medium">{formatEngagement(item.engagement)} views</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          {item.publishedDate 
                            ? new Date(item.publishedDate).toLocaleDateString()
                            : item.scheduledDate 
                              ? `Scheduled: ${new Date(item.scheduledDate).toLocaleDateString()}`
                              : 'Draft'
                          }
                        </span>
                        <div className="flex gap-1">
                          {item.tags.slice(0, 3).map(tag => (
                            <span key={tag} className="px-2 py-1 bg-muted/50 rounded-md text-xs text-muted-foreground">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" className="btn-outline">
                          Edit
                        </Button>
                        <Button size="sm" className="btn-primary">
                          View
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Empty State */}
      {filteredContent.length === 0 && (
        <Card className="card-modern slide-up" style={{ animationDelay: '0.6s' }}>
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 bg-muted/50 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold mb-2">No content found</h3>
            <p className="text-muted-foreground mb-6">
              Try adjusting your filters or create new content
            </p>
            <Button className="btn-primary">
              Create Content
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default ContentPage